import React, { useMemo } from 'react';
import { Empty, Table, Tooltip, Tag, Popover } from 'antd-v5';
import { format } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../../../../../components/Routes/Today/today.css';
import { BadgeStatus } from '../BadgeStatus';
import { isEditable } from '../helpers/isEditable';

function getTimeZone(storeId, appointmentsConfig) {
  const storeConfig = appointmentsConfig?.stores?.find(store => store.id === storeId);
  return storeConfig?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const AppointmentTable = ({
  handleEdit,
  handleMessage,
  data,
  tableProps,
  hideColumns,
  formatDate = "EE MMM dd, yyyy 'at' hh:mma",
  emptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No appointments found" />,
  appointmentsConfig = {},
}) => {
  const columns = useMemo(
    () => [
      {
        title: 'Date & Time',
        dataIndex: 'appointmentDate',
        key: 'appointmentDate',
        render: (item, record) =>
          format(new TZDate(item, getTimeZone(record.store.id, appointmentsConfig)), formatDate),
        hidden: hideColumns?.includes('appointmentDate'),
      },
      {
        title: 'Client Name',
        dataIndex: 'client.id',
        key: 'client.id',
        render: (_, record) => <Link to={`/Clients?client=${record.client.id}`}>{record.client.name}</Link>,
        hidden: hideColumns?.includes('client'),
      },
      {
        title: 'Assigned To',
        dataIndex: 'associate',
        key: 'associate',
        render: (associate, record) =>
          associate ? `${associate.firstName || ''} ${associate.lastName || ''}` : 'Unassigned',
        hidden: hideColumns?.includes('associate'),
      },
      {
        title: 'Store',
        dataIndex: 'store.id',
        key: 'store.id',
        render: (_, record) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span>{record.store?.name || ' - '}</span>
            {record.virtual && (
              <Popover
                content={
                  <div style={{ maxWidth: 250 }}>
                    Meeting details are coordinated by your team. Please message the client your video link or call instructions.
                  </div>
                }
                title="Virtual Appointments"
              >
                <Tag
                  color="#F2F3F6"
                  className="fw-600 fs-10"
                  style={{
                    cursor: 'pointer',
                    color: '#818181',
                    marginTop: '4px',
                  }}
                >
                  VIRTUAL
                </Tag>
              </Popover>
            )}
          </div>
        ),
        hidden: hideColumns?.includes('store'),
      },
      {
        title: 'Reason',
        dataIndex: 'appointmentType.id',
        key: 'appointmentType.id',
        render: (_, record) => record.appointmentType?.name || ' - ',
        hidden: hideColumns?.includes('appointmentType'),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        render: source => {
          switch (source) {
            case 'ASSOCIATE':
              return 'App';
            case 'CLIENT':
              return 'Kiosk';
            default:
              return ' - ';
          }
        },
        hidden: hideColumns?.includes('source'),
      },
      {
        title: 'Status',
        dataIndex: 'status.id',
        key: 'status.id',
        render: (_, record) => <BadgeStatus status={record.status} />,
        hidden: hideColumns?.includes('status'),
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        align: 'left',
        render: (_, record) => (
          <div className="align-text-left">
            {hideColumns?.includes('edit') ? null : (
              <Tooltip title={isEditable(record.status) ? 'Edit appointment' : 'Cannot edit this appointment'}>
                <FontAwesomeIcon
                  icon={['far', 'pencil']}
                  onClick={isEditable(record.status) ? () => handleEdit(record) : undefined}
                  className={`gray ${isEditable(record.status) ? 'pointer' : 'not-allowed opacity-50'}`}
                />
              </Tooltip>
            )}
            {hideColumns?.includes('message') ? null : (
              <Tooltip title="Send message">
                <FontAwesomeIcon
                  className="checkbox fs-19 m-left-18 pointer"
                  color="#818181"
                  icon={['far', 'comment-alt-lines']}
                  onClick={() => handleMessage({ client_id: record.client.id })}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Table
      columns={columns}
      dataSource={data}
      {...tableProps}
      bordered={false}
      className="today-table"
      components={{
        header: {
          cell: props => (
            <th
              {...props}
              className="header-cell-no-separator"
              style={{
                ...props.style,
                backgroundColor: 'transparent',
                color: '#575757',
                '&::before': {
                  display: 'none !important',
                },
              }}
            />
          ),
        },
      }}
      onHeaderRow={() => ({
        className: 'appointment-table-header-row today-table',
        style: {
          background: 'transparent',
          color: '#575757',
          fontWeight: 600,
          fontSize: '13px',
          textAlign: 'left',
          paddingTop: '10px',
          paddingBottom: '20px',
        },
      })}
      locale={{
        emptyText,
      }}
    />
  );
};
